// Multi-tenant shell.
//
// anon                  → LoginPage
// authed, 0 servers     → /onboarding (wizard)
// authed, 1 server      → auto-enter it, → /desk
// authed, 2+, no active → /servers (picker)
// authed, active set    → /desk (and Desk/Setup nav links)

import { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { NavBar } from "./components/NavBar";
import { vpsApi } from "./api/vps";
import { AccountPage } from "./pages/AccountPage";
import { Desk } from "./pages/Desk";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { SleevesPage } from "./pages/SleevesPage";
import { SleeveInsightPage } from "./pages/SleeveInsightPage";
import { SetupPage } from "./pages/SetupPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { SystemPage } from "./pages/SystemPage";

export function App() {
  const { status, activeVpsId } = useAuth();
  if (status === "checking") {
    return <div className="login-shell"><div className="muted">checking session…</div></div>;
  }
  if (status === "anon") {
    return <LoginPage />;
  }
  return (
    <div className="app-shell">
      <NavBar />
      <AutoEnter />
      <Routes>
        {/* Legacy URLs — keep so bookmarks don't 404. */}
        <Route path="/vpses" element={<Navigate to="/system?tab=servers" replace />} />
        <Route path="/history" element={<Navigate to="/analytics" replace />} />
        <Route path="/config" element={<Navigate to="/system?tab=config" replace />} />
        <Route path="/servers" element={<Navigate to="/system?tab=servers" replace />} />
        <Route path="/admin/users" element={<Navigate to="/system?tab=users" replace />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/account" element={<AccountPage />} />

        {/* System (subsumes Config + Servers + Users) */}
        <Route
          path="/system"
          element={<SystemPage />}
        />

        {/* Server-scoped — gated on activeVpsId */}
        <Route
          path="/desk"
          element={
            activeVpsId !== null ? (
              <main className="app-main"><Desk /></main>
            ) : (
              <Navigate to="/system?tab=servers" replace />
            )
          }
        />
        <Route
          path="/analytics"
          element={
            activeVpsId !== null ? (
              <main className="app-main"><AnalyticsDashboard /></main>
            ) : (
              <Navigate to="/system?tab=servers" replace />
            )
          }
        />
        <Route
          path="/sleeves"
          element={
            activeVpsId !== null ? (
              <main className="app-main"><SleevesPage /></main>
            ) : (
              <Navigate to="/system?tab=servers" replace />
            )
          }
        />
        <Route
          path="/sleeves/:id"
          element={
            activeVpsId !== null ? (
              <main className="app-main"><SleeveInsightPage /></main>
            ) : (
              <Navigate to="/system?tab=servers" replace />
            )
          }
        />
        <Route
          path="/setup"
          element={
            activeVpsId !== null ? (
              <main className="app-main"><SetupPage /></main>
            ) : (
              <Navigate to="/system?tab=servers" replace />
            )
          }
        />

        {/* Default landing */}
        <Route
          path="*"
          element={<Navigate to={activeVpsId !== null ? "/desk" : "/system?tab=servers"} replace />}
        />
      </Routes>
    </div>
  );
}

// AutoEnter does the flat-routing magic: on first authed render, look at
// the roster and either drop the user into the wizard (0 servers), auto-
// enter their only server (1), or leave them on the picker (2+).
//
// Lives inside the Router subtree because it uses useNavigate. Rendered
// once per authed session — short-circuits as soon as activeVpsId is set.
function AutoEnter() {
  const { activeVpsId, enterVps } = useAuth();
  const nav = useNavigate();
  const rosterQ = useQuery({
    queryKey: ["vps", "list"],
    queryFn: vpsApi.list,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!rosterQ.isSuccess) return;
    if (activeVpsId !== null) return;
    const roster = rosterQ.data ?? [];
    if (roster.length === 0) {
      if (window.location.pathname !== "/onboarding") {
        nav("/onboarding", { replace: true });
      }
      return;
    }
    if (roster.length === 1) {
      void enterVps(roster[0].id).then(() => {
        nav("/desk", { replace: true });
      });
    }
    // 2+ → leave them wherever the router puts them (defaults to /servers).
  }, [rosterQ.isSuccess, rosterQ.data, activeVpsId, enterVps, nav]);

  return null;
}
